import { createAction, createAsyncAction } from "typesafe-actions";

import EntityType from "@mapmycustomers/shared/enum/EntityType";
import Company from "@mapmycustomers/shared/types/entity/Company";
import Person from "@mapmycustomers/shared/types/entity/Person";
import { EntityPin } from "@mapmycustomers/shared/types/map";
import { CategorizedMapEntries } from "@mapmycustomers/shared/types/map";
import { GeoPoint } from "@mapmycustomers/shared/types/shapes";
import MapViewState from "@mapmycustomers/shared/types/viewModel/MapViewState";

import { MapFetcherPayload } from "@app/types/map/MapFetcher";
import MapPersistedViewportState from "@app/types/map/MapPersistedViewportState";
import MapViewportState from "@app/types/map/MapViewportState";
import { StopVisualsMap } from "@app/util/map/markerStyles/types/StopVisual";

import { EntityRouteAnnotation } from "../types/RouteAnnotation";

export const setGoToLocationPopoverVisibility = createAction(
  "route/routeDetails/setGoToLocationPopoverVisibility"
)<boolean>();

export const initializeRouteViewport = createAsyncAction(
  "route/routeDetails/initializeViewport/request",
  "route/routeDetails/initializeViewport/success",
  "route/routeDetails/initializeViewport/failure"
)<(viewport: MapPersistedViewportState) => void, void, unknown>();

export const persistRouteViewport = createAction(
  "route/routeDetails/persistViewport"
)<MapViewportState>();

export const fetchRouteViewPins = createAsyncAction(
  "route/routeDetails/fetchRouteViewPins/request",
  "route/routeDetails/fetchRouteViewPins/success",
  "route/routeDetails/fetchRouteViewPins/failure"
)<
  MapFetcherPayload,
  CategorizedMapEntries & {
    externalEntries: CategorizedMapEntries;
    pinsCount: number;
  },
  unknown
>();

export const fetchPins = createAsyncAction(
  "route/routeDetails/fetchPins/request",
  "route/routeDetails/fetchPins/success",
  "route/routeDetails/fetchPins/failure"
)<
  MapFetcherPayload,
  CategorizedMapEntries & {
    externalEntries: CategorizedMapEntries;
    pinsCount: number;
  },
  unknown
>();

export const fetchRouteStopVisuals = createAsyncAction(
  "route/routeDetails/fetchRouteStopVisuals/request",
  "route/routeDetails/fetchRouteStopVisuals/success",
  "route/routeDetails/fetchRouteStopVisuals/failure"
)<
  { companyIds: Company["id"][]; personIds: Person["id"][] },
  { stopVisuals: StopVisualsMap },
  unknown
>();

export const applyRouteViewSettings = createAction("route/routeDetails/applyViewSettings")<
  Partial<MapViewState>
>();

// Synchronous: a record's id and type are all an annotation needs, and every caller already holds
// both — a stop click reads them off the stop, a pin click off the pin it was handed. Nothing to
// look up, so nothing to fail.
export const showRouteAnnotation = createAction("route/routeDetails/showAnnotation")<
  Omit<EntityRouteAnnotation, "type">
>();

export const hideRouteAnnotation = createAction("route/routeDetails/hideAnnotation")();

export const showRouteManualStopAnnotation = createAction(
  "route/routeDetails/showManualStopAnnotation"
)<{ address: string; geoPoint: GeoPoint; name: string }>();

export const setRoutePinHover = createAction("route/routeDetails/setPinHover")<
  string | undefined
>();

export const setRouteSelectedPin = createAction("route/routeDetails/setSelectedPin")<
  EntityPin | undefined
>();

export const setRouteFocusedStop = createAction("route/routeDetails/setFocusedStop")<
  EntityPin | undefined
>();

export const focusPin = createAction("route/routeDetails/focusPin")<{
  entityType: EntityType.COMPANY | EntityType.PERSON;
  id: number;
}>();
